/**
 * Referral API Hook
 *
 * Custom hook for managing referral directory API operations.
 * Provides referral CRUD operations using the centralized apiService.
 * Follows existing directory patterns for consistency.
 */

import { useState, useCallback } from 'react';
import { useAPIService, APIError } from '../../../services/apiService';

// US State choices for address form
export interface StateOption {
  name: string;
  value: string;
}

// Facility reference for multi-select
export interface FacilityReference {
  id: number;
  name: string;
}

// Referral Tag for color coding
export interface ReferralTag {
  id: number;
  name: string;
  color: string;
  priority?: number;
}

// Referral interface matching Django ReferralDoctor model
export interface Referral {
  id: number;
  name: string;
  specialty: string;
  notes?: string;
  website_url?: string;
  contact_phone?: string;
  contact_email?: string;
  address_line_1?: string;
  address_line_2?: string;
  address_city?: string;
  address_zip?: string;
  address_state?: string;
  facilities: string[] | FacilityReference[]; // String array for list view, object array for detail
  referral_tag_id?: number | null;
  referral_color?: string; // Computed from tag for list view
  tag?: ReferralTag; // Full tag object for detail view
  display_facilities?: string; // Comma-separated for display
  id_facilities?: number[]; // Facility IDs for form
}

// Referral list data structure
export interface ReferralListData {
  referrals: Referral[];
  facilities: FacilityReference[];
  states: StateOption[];
  specialties: string[];
  referral_tags: ReferralTag[];
}

// Referral creation/update payload
export interface ReferralPayload {
  id?: number;
  name: string;
  specialty: string;
  notes?: string;
  website_url?: string;
  contact_phone?: string;
  contact_email?: string;
  address_line_1?: string;
  address_line_2?: string;
  address_city?: string;
  address_zip?: string;
  address_state?: string;
  facilities: number[]; // Array of facility IDs
  referral_tag_id?: number | null;
}

/**
 * Referral API service hook
 * Provides centralized referral data management with loading states
 */
export const useReferralApi = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiService = useAPIService();

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Handle API errors consistently
   */
  const handleError = useCallback((err: unknown, fallbackMessage: string) => {
    let errorMessage = fallbackMessage;

    if (err instanceof APIError) {
      errorMessage = err.message;
    } else if (err instanceof Error) {
      errorMessage = err.message;
    }

    setError(errorMessage);
    console.error('Referral API Error:', err);
  }, []);

  /**
   * Fetch complete referral list with metadata
   * Endpoint: /directory/directory_get_referral_list
   */
  const fetchReferralList = useCallback(async (): Promise<ReferralListData> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.post<ReferralListData>(
        '/directory/directory_get_referral_list',
        {},
        'Loading referral directory...'
      );

      return response.data;
    } catch (err) {
      handleError(err, 'Failed to fetch referral list');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [apiService, handleError]);

  /**
   * Fetch single referral details
   * Endpoint: /directory/directory_get_referral
   */
  const fetchReferralDetails = useCallback(async (referralId: number): Promise<Referral> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.post<Referral>(
        '/directory/directory_get_referral',
        { id: referralId },
        'Loading referral details...'
      );

      return response.data;
    } catch (err) {
      handleError(err, 'Failed to fetch referral details');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [apiService, handleError]);

  /**
   * Save referral (create or update)
   * Endpoint: /directory/directory_save_referral
   */
  const saveReferral = useCallback(async (payload: ReferralPayload): Promise<Referral> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.post<Referral>(
        '/directory/directory_save_referral',
        payload,
        payload.id && payload.id > 0 ? 'Updating referral...' : 'Creating referral...'
      );

      return response.data;
    } catch (err) {
      handleError(err, 'Failed to save referral');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [apiService, handleError]);

  /**
   * Delete referral (soft delete)
   * Endpoint: /directory/directory_delete_referral
   */
  const deleteReferral = useCallback(async (referralId: number): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.post<{ success: boolean }>(
        '/directory/directory_delete_referral',
        { id: referralId },
        'Deleting referral...'
      );

      return response.data.success;
    } catch (err) {
      handleError(err, 'Failed to delete referral');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [apiService, handleError]);

  return {
    // State
    isLoading,
    error,
    clearError,

    // API methods
    fetchReferralList,
    fetchReferralDetails,
    saveReferral,
    deleteReferral,
  };
};
